import { useMemo } from "react"; // 1. Import useMemo
import apiClient from "./apiClient";
import { Account, User } from "../state/types";

// --- PRIVATE HELPERS ---
const mapAccountResponse = (u: any): Account => ({
  username: u.Username,
  email: u.Attributes.email || 'N/A',
  first_name: u.Attributes.given_name || '',
  last_name: u.Attributes.family_name || '',
  phone_number: u.Attributes.phone_number || '',
  createdDate: new Date(u.UserCreateDate).toLocaleString(),
  lastLoginDate:
    u.LastLoggedInDate === 'Never Logged In'
      ? u.LastLoggedInDate
      : new Date(u.LastLoggedInDate).toLocaleString(),
  status: u.UserStatus,
  role: u.Role || 'Unknown',
  system_access: u.SystemAccess ?? [],
});

// --- THE HOOK ---
export const useAccountApi = () => {
  return useMemo(() => {
    return {
      fetchAccountsApi: async (): Promise<Account[]> => {
        const { data } = await apiClient.get('/user');
        return data.users.map(mapAccountResponse);
      },

      addAccountApi: async (account: Partial<Account>) => {
        return await apiClient.post('/user', account);
      },

      updateAccountApi: async (account: Account) => {
        return await apiClient.put(`/user/${account.username}`, account);
      },

      deleteAccountApi: async (username: string) => {
        return await apiClient.delete(`/user/${username}`);
      },

      resendTempPasswordApi: async (username: string) => {
        return await apiClient.post(`/user/${username}/resend-temp-password`, {
          username,
        });
      },

      loginUserApi: async (): Promise<User> => {
        const { data } = await apiClient.post('/user/login', {});

        const isAutoAdmin =
          data.Role === 'Unknown' &&
          data.LastLogin === 'Never Logged In' &&
          data.Attributes.email?.endsWith('@blackwidowimaging.com');

        const loggedInUser = {
          createdDate: data.UserCreateDate,
          email: data.Attributes.email,
          first_name: data.Attributes.given_name,
          last_name: data.Attributes.family_name,
          phone_number: data.Attributes.phone_number,
          role: isAutoAdmin ? "Admin" : data.Role,
          system_access: data.SystemAccess,
          username: data.Username,
        };
        
        localStorage.setItem('user', JSON.stringify(loggedInUser));

        return loggedInUser
      },
    };
  }, []);
};